import React, { useCallback, useState, useLayoutEffect, useRef } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

import { useSnackbar } from 'notistack';

import { Button, Fade } from '@material-ui/core';

import Ocupation from 'components/Tools/Ocupation';
import { Table } from 'components/Tools/Table';

import { TableContainer, PageContainer } from '../../../../../app/styles';

import { IProfilePermission } from '../../../../../utils/interfaces';

import { savePermissionsRequest } from '../../api/profilePermissions.api';

import SwitchButton from '../Inputs/SwitchButton';
import { IProfileManupuled } from '../../types';

interface IProfilePermissionsTableProps {
  rows: IProfilePermission[];
  profilePermissionsManipuled: IProfileManupuled[];
}

const ProfilePermissionsTable: React.FC<IProfilePermissionsTableProps> = ({
  rows,
  profilePermissionsManipuled,
}) => {
  const permissionRef = useRef<IProfileManupuled[]>([]);
  const [rowsState, setRowsState] = useState<IProfileManupuled[]>([]);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const clientId = Number(localStorage.getItem('@pdamodules::codigoCliente'));

  const traductorMsg = (msg: string) => {
    return i18n.t(msg);
  };

  const handleTogglePermission = useCallback((systemCode: number) => {
    setRowsState(prev => {
      const clicked = prev.find(p => p.codigoSistemaMenu === systemCode);
      if (!clicked) return prev;

      const newPermissao = !clicked.permissao;

      // Se for um pai
      if (!clicked.codigoMenuPai) {
        return prev.map(item => {
          if (item.codigoSistemaMenu === clicked.codigoSistemaMenu) {
            // Atualiza o pai
            return { ...item, permissao: newPermissao };
          }

          if (item.codigoPai === clicked.codigoPai) {
            // Se pai virou false -> força todos filhos para false
            if (!newPermissao) {
              return { ...item, permissao: false };
            }
            // Se pai virou true -> filhos não mudam, ficam como estão
            return item;
          }

          return item;
        });
      }

      // Se for filho
      return prev.map(item =>
        item.codigoSistemaMenu === clicked.codigoSistemaMenu
          ? { ...item, permissao: newPermissao }
          : item
      );
    });
  }, []);

  const handleSavePermissions = useCallback(async () => {
    enqueueSnackbar(`${traductorMsg('screens.profile.saving')}...`, {
      variant: 'info',
    });

    await savePermissionsRequest(clientId, rowsState)
      .then(() => {
        enqueueSnackbar(
          `${traductorMsg('screens.profile.permissionsSaving')} !`,
          {
            variant: 'success',
          }
        );
      })
      .catch(err => {
        enqueueSnackbar(
          `${traductorMsg('screens.user.error')}: ${err.message}`,
          {
            variant: 'error',
          }
        );
      });
  }, [enqueueSnackbar, rowsState, clientId]);

  useLayoutEffect(() => {
    permissionRef.current = profilePermissionsManipuled;
    setRowsState(profilePermissionsManipuled);
  }, [profilePermissionsManipuled]);

  return (
    <Fade in timeout={1000} unmountOnExit>
      <PageContainer withOutPadding>
        <TableContainer>
          <Table<IProfileManupuled>
            rows={rowsState}
            getRows={[
              {
                onClick: () => handleSavePermissions(),
                renderItem: () => (
                  <Button
                    className="secundaryButton"
                    type="button"
                    variant="contained"
                    style={{ background: '#46BE7D', color: '#fff' }}
                  >
                    {t('screens.profile.save')}
                  </Button>
                ),
              },
            ]}
            columns={[
              {
                title: String(t('screens.profile.menuDescription')),
                type: 'string',
                props: ['descricaoMenuPai'],
                cssTitle: {
                  width: '1%',
                },
                cssProps: {
                  width: '1%',
                  paddingRight: '14px',
                },
                renderItem: row => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <Ocupation
                          compareColumn={row.descricaoMenuPai}
                          text={row.descricaoMenuPai}
                        />
                      </div>
                      <SwitchButton
                        name={`${row.descricaoMenuPai}`}
                        isRow
                        handleInputChange={() => {
                          handleTogglePermission(row.codigoSistemaMenu);
                        }}
                        defaultValueChecked={Boolean(row.permissao)}
                      />
                    </div>
                  );
                },
              },
              {
                title: String(t('screens.profile.menuDescription')),
                type: 'string',
                props: ['descricaoMenu'],
                display: row =>
                  row.codigoMenuPai !== undefined ||
                  row.descricaoMenu?.toLowerCase() === 'módulos',
                cssProps: {
                  width: '1%',
                },
                cssTitle: {
                  width: '1%',
                },
                renderItem: (row: IProfileManupuled) => {
                  return (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                      }}
                      className="row-actions-button-detail"
                    >
                      <p>
                        {row.descricaoMenu.toLowerCase() === 'módulos'
                          ? ''
                          : row.descricaoMenu}
                      </p>
                    </div>
                  );
                },
              },
              {
                title: String(t('screens.profileTable.permissions')),
                type: 'string',
                props: ['codigoSistemaMenu'],
                display: row =>
                  row.codigoMenuPai !== undefined ||
                  row.descricaoMenu?.toLowerCase() === 'módulos',
                cssProps: {
                  width: '1%',
                },
                cssTitle: {
                  width: '1%',
                },
                renderItem: row => {
                  const menuPai = rowsState.find(
                    p => p.codigoGrupo === row.codigoGrupo && !p.codigoMenuPai
                  );
                  return (
                    <>
                      {row.codigoMenuPai && (
                        <SwitchButton
                          name={`${row.codigoSistemaMenu}`}
                          isRow
                          handleInputChange={() => {
                            handleTogglePermission(row.codigoSistemaMenu);
                          }}
                          defaultValueChecked={
                            menuPai?.permissao === true
                              ? Boolean(row.permissao)
                              : false
                          }
                        />
                      )}
                    </>
                  );
                },
              },
            ]}
            defaultNumberOfRows={rows.length}
          />
        </TableContainer>
      </PageContainer>
    </Fade>
  );
};

export default ProfilePermissionsTable;
